import { CheckIcon } from '@chakra-ui/icons'
import {
  Button,
  Center,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react'
import { AppContext, useMe } from '@hb/react'
import {
  ClearedHistoryItem,
  colors,
  getDateString,
  getDateTimeString,
  PopulatedAssessment,
} from '@hb/shared'
import { arrayUnion } from 'firebase/firestore'
import { PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useUpdateDoc } from '../../../../hooks/backend/useUpdateDoc'
import { UserBadge } from '../../UserBadge'

const LegacyClearedHistoryView = ({
  clearedOn,
  clearedBy,
}: {
  clearedOn: number | null | undefined
  clearedBy: string | null | undefined
}) =>
  clearedOn ? (
    <Flex py={1} px={2} borderRadius={4} gap={1.5} bg="gray.50" align="center">
      {clearedBy ? <UserBadge userGroup="admin" userId={clearedBy} /> : null}
      <Text fontWeight={600} color="gray.500">
        Cleared on {getDateString(clearedOn, 'short')}
      </Text>
    </Flex>
  ) : null

const ClearedHistoryItemView = ({ item }: { item: ClearedHistoryItem }) => {
  const { cleared, on, by, group } = item
  return (
    <Flex align="center" p={2} w="100%">
      <Text
        position="relative"
        fontSize="xs"
        fontWeight={500}
        color={cleared ? 'green.600' : 'gray.600'}
        fontFamily="Hero-New">
        {cleared ? 'MARKED CLEARED' : 'UNMARKED'}
      </Text>
      <Flex ml="auto" align="center" gap={1}>
        <Text fontSize="xs" fontWeight={600} color="gray.600">
          {getDateTimeString(on, 'short')}
        </Text>
        <UserBadge userId={by} userGroup={group} />
      </Flex>
    </Flex>
  )
}

// default to clearedOn and clearedBy if clearedHistory is empty
const ClearedHistoryView = ({
  selectedAssessment,
}: {
  selectedAssessment: PopulatedAssessment | null
}) => {
  const { clearedOn, clearedBy, clearedHistory } = selectedAssessment ?? {}
  if (!clearedHistory?.length) {
    return <LegacyClearedHistoryView clearedOn={clearedOn} clearedBy={clearedBy} />
  }
  return (
    <VStack maxH="300px" overflowY="auto" divider={<StackDivider />} w="100%" spacing={0}>
      {clearedHistory.toReversed().map(item => (
        <ClearedHistoryItemView key={item.on} item={item} />
      ))}
    </VStack>
  )
}

const AssessmentClearedPopover = ({
  children,
  selectedAssessment,
}: PropsWithChildren<{ selectedAssessment: PopulatedAssessment | null }>) =>
  selectedAssessment?.clearedOn || selectedAssessment?.clearedHistory?.length ? (
    <Popover trigger="hover">
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent w="350px">
        <PopoverBody borderRadius={4} p={0} bg="gray.50">
          <ClearedHistoryView selectedAssessment={selectedAssessment} />
        </PopoverBody>
        <PopoverArrow bg="gray.50" />
      </PopoverContent>
    </Popover>
  ) : (
    children
  )
const PracticeAssessmentClearedSection = ({ mobileView }: { mobileView: boolean }) => {
  const {
    selectedAssessment: { populated: selectedAssessment },
  } = useProfile()
  const { clearedOn } = selectedAssessment ?? {}
  const { appName } = useContext(AppContext)
  const text = clearedOn ? 'CLEARED FOR COVERAGE' : 'NOT CLEARED FOR COVERAGE'
  const color = clearedOn ? colors.green.hex : '#ababab'
  return (
    <AssessmentClearedPopover selectedAssessment={selectedAssessment}>
      <HStack
        bg={clearedOn ? 'transparent' : 'whitesmoke'}
        px={mobileView ? 1 : 2}
        cursor="default"
        py={mobileView ? 0.5 : 1}
        spacing={1}
        borderRadius={6}
        border={`1px solid ${clearedOn ? 'transparent' : color}`}>
        {clearedOn ? (
          <Center p={1} borderRadius={6} bg={color}>
            <CheckIcon width="14px" height="14px" color="white" />
          </Center>
        ) : null}
        {clearedOn && appName === 'app' ? null : (
          <Text
            fontSize={mobileView ? 'xs' : 'sm'}
            fontWeight={500}
            lineHeight={1}
            whiteSpace="nowrap"
            fontFamily="Hero-New"
            color={color}>
            {text}
          </Text>
        )}
      </HStack>
    </AssessmentClearedPopover>
  )
}

const AdminAssessmentClearedSection = ({ mobileView }: { mobileView: boolean }) => {
  const {
    selectedAssessment: { populated: selectedAssessment, snippetRef },
  } = useProfile()
  const { clearedOn } = selectedAssessment ?? {}

  const me = useMe()
  const [hovered, setHovered] = useState(false)
  const update = useUpdateDoc('cleared status')
  const [isLoading, setIsLoading] = useState(false)
  const handleClick = useCallback(async () => {
    if (!me) return
    setIsLoading(true)
    const now = Date.now()
    try {
      const clearedHistoryItem: ClearedHistoryItem = {
        cleared: !clearedOn,
        on: now,
        by: me.uid,
        group: 'admin',
      }
      await update(snippetRef, '', {
        clearedOn: clearedOn ? null : now,
        clearedBy: clearedOn ? null : me.uid,
        clearedHistory: arrayUnion(clearedHistoryItem),
      })
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [clearedOn, snippetRef, update, me])

  const color = useMemo(
    () => (clearedOn || hovered ? colors.green.hex : '#ababab'),
    [clearedOn, hovered],
  )

  const button = (
    <Button
      onClick={handleClick}
      isLoading={isLoading}
      transition="all 300ms"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      bg="whitesmoke"
      w="200px"
      fontFamily="Hero-New"
      overflow="hidden"
      size={mobileView ? 'xs' : 'sm'}
      fontSize="xs"
      gap={1}
      px={0}
      minW="0"
      borderRadius={6}
      border={`1px solid ${color}`}>
      <Flex px={3} justify="center" align="center" gap={1.5} w="100%">
        {clearedOn ? (
          <Center p={1} borderRadius={6} bg={color}>
            <CheckIcon width="14px" color="white" />
          </Center>
        ) : null}
        <Text lineHeight={1} color={color}>
          {clearedOn ? `CLEARED ${getDateString(clearedOn, 'short')}` : 'NOT CLEARED FOR COVERAGE'}
        </Text>
      </Flex>
    </Button>
  )

  return (
    <HStack>
      <AssessmentClearedPopover selectedAssessment={selectedAssessment}>
        {button}
      </AssessmentClearedPopover>
    </HStack>
  )
}

export const AssessmentClearedSection = ({ mobileView }: { mobileView: boolean }) => {
  const { appName } = useContext(AppContext)
  return appName === 'providers-app' ? (
    <PracticeAssessmentClearedSection mobileView={mobileView} />
  ) : (
    <AdminAssessmentClearedSection mobileView={mobileView} />
  )
}
